import type { EventCallbackFunction, EventMessage } from "@azure/msal-browser";
import { EventType, InteractionType } from "@azure/msal-browser";
import { LAST_COURSE_DASHBOARD_KEY } from "@/hooks/useRememberCourseDashboard";

/**
 * MSAL itself brings the user back to the page that started sign-in
 * (`navigateToLoginRequestUrl`, on by default) or to a `redirectStartPage`.
 * These helpers only cover what MSAL has no signal for: a sign-in abandoned
 * with the browser's Back button, and the course to reopen after logout.
 */

/** The page that started the current Microsoft sign-in. */
const LOGIN_STARTED_KEY = "loginStartedFrom";
/** The course or preview page to reopen after logout. */
const LOGOUT_RETURN_KEY = "logoutReturnUrl";

/** App-relative path. Rejects a protocol-relative URL such as `//host`. */
const isAppPath = (path: string) =>
  path.startsWith("/") && !path.startsWith("//");

const markLoginStarted = (path: string) => {
  sessionStorage.setItem(LOGIN_STARTED_KEY, path);
};

/**
 * True when `path` started a sign-in that has not finished. On a fresh page
 * load without an account this means the user came back without signing in.
 */
const loginStartedFrom = (path: string) =>
  sessionStorage.getItem(LOGIN_STARTED_KEY) === path;

/** Sign-in finished or was given up. Drop both stored return URLs. */
const finishLogin = () => {
  sessionStorage.removeItem(LOGIN_STARTED_KEY);
  sessionStorage.removeItem(LOGOUT_RETURN_KEY);
};

/**
 * Keep the open course or preview URL across logout so the next sign-in can
 * reopen it. A page outside those routes is ignored. The login start is
 * cleared so this return is not treated as a cancelled sign-in, and the
 * remembered dashboard so the next account does not inherit it.
 */
const rememberCourseAfterLogout = () => {
  sessionStorage.removeItem(LOGIN_STARTED_KEY);
  sessionStorage.removeItem(LAST_COURSE_DASHBOARD_KEY);
  const path =
    window.location.pathname + window.location.search + window.location.hash;
  const inCourse = path.startsWith("/course/") || path.startsWith("/preview/");
  if (!isAppPath(path) || !inCourse) return;
  sessionStorage.setItem(LOGOUT_RETURN_KEY, path);
};

const isRedirectLogoutEvent = (event: EventMessage, type: EventType) =>
  event.eventType === type &&
  event.interactionType === InteractionType.Redirect;

/**
 * Logout leaves this document blank, and Back from the Microsoft logout
 * screen may restore it from the back/forward cache with MSAL still "logging
 * out". MSAL handles that for sign-in (it rejects the login as cancelled)
 * but not for logout, so reload: the course URL can then start sign-in again.
 * Returns a function that stops watching.
 */
const watchAbandonedLogout = (
  msal: { addEventCallback: (callback: EventCallbackFunction) => unknown },
  reloadPage: () => void = () => {
    window.location.reload();
  }
) => {
  let leftForLogoutScreen = false;
  msal.addEventCallback((event: EventMessage) => {
    if (isRedirectLogoutEvent(event, EventType.LOGOUT_START)) {
      leftForLogoutScreen = true;
    }
    if (isRedirectLogoutEvent(event, EventType.LOGOUT_FAILURE)) {
      leftForLogoutScreen = false;
    }
  });
  const reloadRestoredLogoutPage = (event: PageTransitionEvent) => {
    if (!event.persisted || !leftForLogoutScreen) return;
    leftForLogoutScreen = false;
    reloadPage();
  };
  window.addEventListener("pageshow", reloadRestoredLogoutPage);
  return () => {
    window.removeEventListener("pageshow", reloadRestoredLogoutPage);
  };
};

export {
  LOGIN_STARTED_KEY,
  LOGOUT_RETURN_KEY,
  finishLogin,
  isAppPath,
  loginStartedFrom,
  markLoginStarted,
  rememberCourseAfterLogout,
  watchAbandonedLogout,
};
